import {api, routes} from "@/plugins/axios";
import {defineStore} from "pinia";
import {ref, computed} from "vue";

export const useBatchStatusStore = defineStore('batchStatus', () => {
    const batches = ref([])
    const activeBatches = ref(new Map())
    const isLoading = ref(false)
    const error = ref(null)
    const pollingInterval = ref(null)
    const dismissTimers = ref(new Map())

    const meta = ref({
        current_page: 1,
        from: 0,
        last_page: 1,
        per_page: 15,
        to: 0,
        total: 0,
    })
    const links = ref([])

    const POLLING_INTERVAL_MS = 2000
    const AUTO_DISMISS_DELAY_MS = 30000

    const activeBatchesList = computed(() => {
        return Array.from(activeBatches.value.values())
            .filter(batch => !batch.is_finished || batch.dismissCountdown > 0)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    })

    const recentBatches = computed(() => {
        return batches.value.filter(batch => batch.is_finished)
    })

    const hasActiveBatches = computed(() => activeBatchesList.value.length > 0)

    async function fetchBatches(organizationSlug, page = 1, perPage = 15, append = false) {
        if (page === 1) {
            isLoading.value = true
        }
        error.value = null

        try {
            const response = await api.get(routes.batches.list(organizationSlug), {
                params: { page, per_page: perPage }
            })

            const newBatches = response.data.data || []

            if (append && page > 1) {
                // Append new batches, avoiding duplicates
                const existingIds = new Set(batches.value.map(b => b.id))
                const uniqueNewBatches = newBatches.filter(b => !existingIds.has(b.id))
                batches.value = [...batches.value, ...uniqueNewBatches]
            } else {
                batches.value = newBatches
            }

            meta.value = response.data.meta || meta.value
            links.value = response.data.links || []

            const hasActiveBatch = newBatches.some(batch => !batch.is_finished)

            newBatches.forEach(batch => {
                if (!batch.is_finished) {
                    trackBatch(organizationSlug, batch.id, batch.name, batch)
                }
            })

            if (hasActiveBatch && !pollingInterval.value) {
                startPolling(organizationSlug)
            }

            return { success: true, data: batches.value }
        } catch (err) {
            error.value = err.response?.data?.message || 'Failed to load batches'
            return { success: false, error: err }
        } finally {
            isLoading.value = false
        }
    }

    function resetBatches() {
        batches.value = []
        meta.value = {
            current_page: 1,
            from: 0,
            last_page: 1,
            per_page: 15,
            to: 0,
            total: 0,
        }
        links.value = []
    }

    async function fetchBatchStatus(organizationSlug, batchId) {
        try {
            const response = await api.get(routes.batches.show(organizationSlug, batchId))
            return response.data.data
        } catch (err) {
            console.error('Failed to fetch batch status:', err)
            return null
        }
    }

    function trackBatch(organizationSlug, batchId, batchName, initialData = null) {
        if (!activeBatches.value.has(batchId)) {
            activeBatches.value.set(batchId, {
                id: batchId,
                name: batchName,
                total_jobs: initialData?.total_jobs ?? 0,
                pending_jobs: initialData?.pending_jobs ?? 0,
                failed_jobs: initialData?.failed_jobs ?? 0,
                processed_jobs: initialData?.processed_jobs ?? 0,
                progress_percent: initialData?.progress_percent ?? 0,
                is_finished: initialData?.is_finished ?? false,
                is_cancelled: initialData?.is_cancelled ?? false,
                meta: initialData?.meta ?? null,
                created_at: initialData?.created_at ?? new Date().toISOString(),
                finished_at: initialData?.finished_at ?? null,
                organizationSlug,
                dismissCountdown: 0
            })

            startPolling(organizationSlug)
        }
    }

    function startPolling() {
        if (pollingInterval.value) return

        pollingInterval.value = setInterval(async () => {
            const activeBatchIds = Array.from(activeBatches.value.keys())

            for (const batchId of activeBatchIds) {
                const batch = activeBatches.value.get(batchId)

                // The batch can be dismissed while a previous iteration awaited.
                if (!batch) {
                    continue
                }

                if (batch.is_finished && batch.dismissCountdown <= 0) {
                    continue
                }

                const updatedBatch = await fetchBatchStatus(batch.organizationSlug, batchId)

                if (updatedBatch) {
                    const currentBatch = activeBatches.value.get(batchId)

                    if (!currentBatch) {
                        continue
                    }

                    activeBatches.value.set(batchId, {
                        ...currentBatch,
                        ...updatedBatch,
                        organizationSlug: currentBatch.organizationSlug,
                        dismissCountdown: currentBatch.dismissCountdown
                    })

                    if (updatedBatch.is_finished && !currentBatch.is_finished) {
                        startAutoDismiss(batchId)
                    }

                    const batchIndex = batches.value.findIndex(b => b.id === batchId)
                    if (batchIndex !== -1) {
                        batches.value[batchIndex] = {
                            ...batches.value[batchIndex],
                            ...updatedBatch
                        }
                    }
                }
            }

            const stillActive = Array.from(activeBatches.value.values())
                .some(b => !b.is_finished || b.dismissCountdown > 0)

            if (!stillActive) {
                stopPolling()
            }
        }, POLLING_INTERVAL_MS)
    }

    function stopPolling() {
        if (pollingInterval.value) {
            clearInterval(pollingInterval.value)
            pollingInterval.value = null
        }
    }

    function startAutoDismiss(batchId) {
        const batch = activeBatches.value.get(batchId)
        if (!batch) return

        activeBatches.value.set(batchId, {
            ...batch,
            dismissCountdown: AUTO_DISMISS_DELAY_MS / 1000
        })

        const countdownInterval = setInterval(() => {
            const currentBatch = activeBatches.value.get(batchId)
            if (!currentBatch) {
                clearInterval(countdownInterval)
                dismissTimers.value.delete(batchId)
                return
            }

            const newCountdown = currentBatch.dismissCountdown - 1

            if (newCountdown <= 0) {
                clearInterval(countdownInterval)
                dismissTimers.value.delete(batchId)
                activeBatches.value.delete(batchId)
            } else {
                activeBatches.value.set(batchId, {
                    ...currentBatch,
                    dismissCountdown: newCountdown
                })
            }
        }, 1000)

        dismissTimers.value.set(batchId, countdownInterval)
    }

    function dismissBatch(batchId) {
        const timer = dismissTimers.value.get(batchId)
        if (timer) {
            clearInterval(timer)
            dismissTimers.value.delete(batchId)
        }
        activeBatches.value.delete(batchId)
    }

    function formatBatchName(name) {
        if (!name) return 'Unknown Operation'

        const operation = name.split(':')[0]

        const nameMap = {
            'update_member_profile': 'Update Member Profiles',
            'reactivate_profiles': 'Reactivate Profiles',
            'deactivate_profiles': 'Deactivate Profiles',
            'resend_invitations': 'Resend Invitations',
            'wipe_members': 'Delete Members',
            'add_to_groups': 'Add to Groups',
            'remove_from_groups': 'Remove from Groups',
            'convert_to_nonactive': 'Convert to Data Record',
            'activate_data_records': 'Activate Data Records',
            'export_members': 'Export Members',
            'export_benefit_claims': 'Export Benefit Claims',
            'change_status': 'Change Benefit Claim Status',
            'reconciliation': 'Reconciliation',
            'reconciliation-apply': 'Apply Reconciliation',
            'reconciliation-rollback': 'Rollback Reconciliation'
        }

        return nameMap[operation] || operation.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    function $reset() {
        stopPolling()
        dismissTimers.value.forEach(timer => clearInterval(timer))
        dismissTimers.value.clear()
        batches.value = []
        activeBatches.value.clear()
        isLoading.value = false
        error.value = null
        meta.value = {
            current_page: 1,
            from: 0,
            last_page: 1,
            per_page: 15,
            to: 0,
            total: 0,
        }
        links.value = []
    }

    return {
        batches,
        activeBatches,
        activeBatchesList,
        recentBatches,
        hasActiveBatches,
        isLoading,
        error,
        meta,
        links,
        fetchBatches,
        fetchBatchStatus,
        trackBatch,
        dismissBatch,
        formatBatchName,
        startPolling,
        stopPolling,
        resetBatches,
        $reset
    }
})
